import { atom, useRecoilState } from "recoil";
import { useCallback, useEffect, useRef } from "react";
import { localStorageDB } from "@/Data/LocalStorage";
import {
    getCharacter,
    generateCharacter as generateCharacterAPI,
    getCharacterTask,
    UserCharacter,
} from "@/Data/World";

export type PollingStatus = 'idle' | 'polling' | 'completed' | 'failed';

type PersonaCharacterState = {
    characterPic:  Record<string, string>;
    sourceImage:   Record<string, string>;
    pollingStatus: Record<string, PollingStatus>;
    taskIds:       Record<string, string>;
    error:         Record<string, string | null>;
};

const STORAGE_KEY = 'personatest:character';
const POLL_INTERVAL = 2000;

const loadFromStorage = (): PersonaCharacterState => {
    try {
        const stored = localStorageDB.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            return {
                characterPic:  parsed.characterPic  ?? {},
                sourceImage:   parsed.sourceImage   ?? {},
                pollingStatus: parsed.pollingStatus ?? {},
                taskIds:       parsed.taskIds       ?? {},
                error:         {},
            };
        }
    } catch (e) { console.error('Failed to load PersonaCharacter from storage:', e); }
    return { characterPic: {}, sourceImage: {}, pollingStatus: {}, taskIds: {}, error: {} };
};

const saveToStorage = (state: PersonaCharacterState): void => {
    try {
        localStorageDB.setItem(STORAGE_KEY, JSON.stringify({
            characterPic:  state.characterPic,
            sourceImage:   state.sourceImage,
            pollingStatus: state.pollingStatus,
            taskIds:       state.taskIds,
        }));
    } catch (e) { console.error('Failed to save PersonaCharacter to storage:', e); }
};

const personaCharacterState = atom<PersonaCharacterState>({
    key: 'personaCharacterState',
    default: loadFromStorage(),
});

export const usePersonaCharacter = (addressid: string) => {
    const [state, setState] = useRecoilState(personaCharacterState);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const characterPic  = state.characterPic[addressid] ?? null;
    const sourceImage   = state.sourceImage[addressid] ?? null;
    const pollingStatus = state.pollingStatus[addressid] ?? 'idle';
    const taskId        = state.taskIds[addressid] ?? null;
    const error         = state.error[addressid] ?? null;

    const fetchCharacter = useCallback(async (): Promise<UserCharacter | null> => {
        const data = await getCharacter(addressid);
        if (data?.image) {
            setState(prev => {
                const updated = {
                    ...prev,
                    characterPic:  { ...prev.characterPic, [addressid]: data.image },
                    sourceImage:   { ...prev.sourceImage, [addressid]: data.source_image },
                    pollingStatus: { ...prev.pollingStatus, [addressid]: 'idle' as PollingStatus },
                };
                saveToStorage(updated);
                return updated;
            });
        }
        return data;
    }, [addressid, setState]);

    const startGeneration = useCallback(async (sourceImage: string): Promise<string | null> => {
        setState(prev => {
            const updated = {
                ...prev,
                pollingStatus: { ...prev.pollingStatus, [addressid]: 'polling' as PollingStatus },
                error:         { ...prev.error, [addressid]: null },
            };
            saveToStorage(updated);
            return updated;
        });

        const newTaskId = await generateCharacterAPI({ source_image: sourceImage, addressid });

        if (!newTaskId) {
            setState(prev => {
                const updated = {
                    ...prev,
                    pollingStatus: { ...prev.pollingStatus, [addressid]: 'failed' as PollingStatus },
                    error:         { ...prev.error, [addressid]: 'Failed to start generation' },
                };
                saveToStorage(updated);
                return updated;
            });
            return null;
        }

        setState(prev => {
            const updated = { ...prev, taskIds: { ...prev.taskIds, [addressid]: newTaskId } };
            saveToStorage(updated);
            return updated;
        });

        return newTaskId;
    }, [addressid, setState]);

    const cancelGeneration = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        setState(prev => {
            const { [addressid]: _, ...restTaskIds } = prev.taskIds;
            const updated = {
                ...prev,
                pollingStatus: { ...prev.pollingStatus, [addressid]: 'idle' as PollingStatus },
                taskIds:       restTaskIds,
                error:         { ...prev.error, [addressid]: null },
            };
            saveToStorage(updated);
            return updated;
        });
    }, [addressid, setState]);

    const clearError = useCallback(() => {
        setState(prev => ({ ...prev, error: { ...prev.error, [addressid]: null } }));
    }, [addressid, setState]);

    // Auto-polling: taskId가 있으면 polling 시작
    useEffect(() => {
        if (!taskId) { return; }

        const poll = async () => {
            const task = await getCharacterTask(taskId);

            if (!task) {
                if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
                setState(prev => {
                    const { [addressid]: _, ...restTaskIds } = prev.taskIds;
                    const updated = {
                        ...prev,
                        pollingStatus: { ...prev.pollingStatus, [addressid]: 'failed' as PollingStatus },
                        taskIds:       restTaskIds,
                        error:         { ...prev.error, [addressid]: 'Failed to get task status' },
                    };
                    saveToStorage(updated);
                    return updated;
                });
                return;
            }

            if (task.status === 'completed' && task.image) {
                if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
                setState(prev => {
                    const { [addressid]: _, ...restTaskIds } = prev.taskIds;
                    const updated = {
                        ...prev,
                        characterPic:  { ...prev.characterPic, [addressid]: task.image! },
                        pollingStatus: { ...prev.pollingStatus, [addressid]: 'completed' as PollingStatus },
                        taskIds:       restTaskIds,
                        error:         { ...prev.error, [addressid]: null },
                    };
                    saveToStorage(updated);
                    return updated;
                });
                return;
            }

            if (task.status === 'failed') {
                if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
                setState(prev => {
                    const { [addressid]: _, ...restTaskIds } = prev.taskIds;
                    const updated = {
                        ...prev,
                        pollingStatus: { ...prev.pollingStatus, [addressid]: 'failed' as PollingStatus },
                        taskIds:       restTaskIds,
                        error:         { ...prev.error, [addressid]: 'Character generation failed' },
                    };
                    saveToStorage(updated);
                    return updated;
                });
            }
        };

        poll();
        pollingRef.current = setInterval(poll, POLL_INTERVAL);

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [addressid, taskId, setState]);

    return {
        characterPic,
        sourceImage,
        pollingStatus,
        error,
        fetchCharacter,
        startGeneration,
        cancelGeneration,
        clearError,
    };
};
